import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { FormError } from "@/components/ui/form-error";
import { LogOut, User, ArrowLeft, ChevronDown } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useRole } from "@/hooks/useRole";
import { Role } from "@/types/auth";
import { useNavigate, useLocation } from "react-router-dom";
import OdilonLogo from "@/assets/odilon/Odilon_02.svg";
import { CashEntryModal } from "@/components/cash/CashEntryModal";
import { useCashEntry } from "@/contexts/CashEntryContext";
import { useCashEntryApi } from "@/hooks/useCashEntryApi";
import { isSessionExpiredError } from "@/lib/authApi";
import { useOrdersApiService } from "@/hooks/useOrdersApi";
import { useBackNavigation } from "@/hooks/useBackNavigation";

interface TopBarProps {
  showBack?: boolean;
}

/** CSS variable holding the rendered TopBar height; consumed by the app layout. */
const TOPBAR_HEIGHT_CSS_VAR = "--topbar-height";

export function TopBar({ showBack = false }: TopBarProps) {
  const {
    signOut,
    displayName,
    user,
    location: userLocation,
    loading: authLoading,
  } = useAuth();
  const { role } = useRole();
  const navigate = useNavigate();
  const location = useLocation();
  const { backLabel, goBack } = useBackNavigation();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const barRef = useRef<HTMLDivElement>(null);
  const {
    setHasEnteredStartAmount,
    isStartModalOpen,
    openStartModal,
    closeStartModal,
    isSkipAllowed,
    isMandatory,
  } = useCashEntry();
  const [isEndModalOpen, setEndModalOpen] = useState(false);
  const initializedUserId = useRef<string | null>(null);
  const userId = user?.id;
  const [initialCheckError, setInitialCheckError] = useState<string | null>(null);
  const [isInitialCheckPending, setInitialCheckPending] = useState(false);
  const [initialCheckAttempt, setInitialCheckAttempt] = useState(0);
  const [endAmountError, setEndAmountError] = useState<string | null>(null);
  const { createCashEntry, updateCashEntry, getTodaysCashEntryForUser } =
    useCashEntryApi();
  const ordersApi = useOrdersApiService();

  const isHomePage = location.pathname === "/";

  // Check if user is Boutique/Fleuriste (drives the cash register workflow)
  const isShopUser = role === Role.BOUTIQUE || role === Role.FLEURISTE;
  // Atelier is location-scoped like a shop (shows its location) but has no
  // register, so it is excluded from the cash-entry workflow.
  const showsLocation = isShopUser || role === Role.ATELIER;

  useEffect(() => {
    initializedUserId.current = null;
    setInitialCheckError(null);
    setInitialCheckPending(false);
  }, [userId]);

  // Only a successful lookup completes initialization for this user.
  useEffect(() => {
    if (
      authLoading || !isShopUser || !userId || initializedUserId.current === userId
    ) {
      return;
    }
    const controller = new AbortController();
    let active = true;
    setInitialCheckPending(true);

    const initializeCashEntry = async () => {
      try {
        const today = new Date().toISOString().split("T")[0];
        const entry = await getTodaysCashEntryForUser(today, controller.signal);
        if (!active) return;

        setHasEnteredStartAmount(
          !!entry && entry.start_amount !== null && entry.end_amount === null,
        );
        if (!entry || entry.start_amount === null) {
          openStartModal();
        }
        initializedUserId.current = userId;
        setInitialCheckError(null);
      } catch (error) {
        if (!active || controller.signal.aborted) return;
        // Authentication failures already clear the session and show sign-in.
        if (!isSessionExpiredError(error)) {
          setInitialCheckError(
            "Impossible de vérifier l'ouverture de caisse. Veuillez réessayer.",
          );
        }
      } finally {
        if (active) setInitialCheckPending(false);
      }
    };
    void initializeCashEntry();

    return () => {
      active = false;
      controller.abort();
    };
  }, [
    authLoading,
    isShopUser,
    userId,
    initialCheckAttempt,
    openStartModal,
    getTodaysCashEntryForUser,
    setHasEnteredStartAmount,
  ]);

  // Clear the check flag on logout
  const handleLogout = () => {
    sessionStorage.removeItem("startModalShown");
    initializedUserId.current = null;
    setInitialCheckError(null);
    signOut();
  };

  const handleStartSubmit = async (amount: number) => {
    if (user && userLocation) {
      try {
        const locations = await ordersApi.fetchLocations();
        const userStore = locations.find((loc) => loc.name === userLocation);

        if (!userStore) {
          console.error("User's location not found:", userLocation);
          return;
        }

        const today = new Date().toISOString().split("T")[0];
        await createCashEntry({
          date: today,
          start_amount: amount,
          store: userStore.id,
        });
        setHasEnteredStartAmount(true);
        closeStartModal();
      } catch (error) {
        console.error("Failed to submit start amount:", error);
      }
    }
  };

  const handleEndSubmit = async (amount: number) => {
    setEndAmountError(null);
    try {
      const today = new Date().toISOString().split("T")[0];
      let entry = await getTodaysCashEntryForUser(today);

      if (!entry) {
        // If no entry exists, try to create one with end_amount only
        // This handles the case where user skipped start amount but needs to close
        if (user && userLocation) {
          const locations = await ordersApi.fetchLocations();
          const userStore = locations.find((loc) => loc.name === userLocation);

          if (!userStore) {
            setEndAmountError(
              "Erreur: Impossible de trouver votre boutique. Veuillez réessayer.",
            );
            return;
          }

          entry = await createCashEntry({
            date: today,
            start_amount: null,
            end_amount: amount,
            store: userStore.id,
          });
        } else {
          setEndAmountError(
            "Erreur: Impossible de créer l'entrée de caisse. Veuillez réessayer.",
          );
          return;
        }
      } else {
        // Update existing entry - only send end_amount to avoid triggering start_amount permission check
        await updateCashEntry(entry.id, { end_amount: amount });
      }

      // Reset hasEnteredStartAmount and proceed with logout
      setHasEnteredStartAmount(false);
      handleLogout();
    } catch (error) {
      console.error("Failed to submit end amount:", error);
      setEndAmountError(
        "Une erreur est survenue lors de l'enregistrement. Veuillez réessayer.",
      );
    }
  };

  const handleLogoutClick = () => {
    if (isShopUser) {
      // Always show end amount modal for shop users on logout
      setEndModalOpen(true);
    } else {
      handleLogout();
    }
    setDropdownOpen(false);
  };

  const shouldShowBackButton = () => {
    return showBack && !isHomePage;
  };

  // Publish the rendered bar height (including the safe-area inset) so the
  // layout can offset page content by exactly that amount on every device.
  useEffect(() => {
    const bar = barRef.current;
    if (!bar) return;

    const publishHeight = () => {
      document.documentElement.style.setProperty(
        TOPBAR_HEIGHT_CSS_VAR,
        `${Math.ceil(bar.getBoundingClientRect().height)}px`,
      );
    };

    publishHeight();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", publishHeight);
      return () => window.removeEventListener("resize", publishHeight);
    }
    const observer = new ResizeObserver(publishHeight);
    observer.observe(bar);
    return () => observer.disconnect();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setDropdownOpen(false);
      }
    };

    if (dropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownOpen]);

  return (
    <>
      <div
        ref={barRef}
        className="bg-white border-b fixed top-0 left-0 right-0 z-50 shadow-sm pt-[env(safe-area-inset-top)]"
      >
        <div className="px-4 sm:px-6 py-4">
          <div className="grid grid-cols-3 items-center">
            {/* Left Side - Back Button */}
            <div className="flex min-w-0 items-center gap-4">
              {shouldShowBackButton() && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={goBack}
                  className="h-9 max-w-full md:h-12 md:text-base"
                  testID="topbar-back-button"
                  title={backLabel}
                >
                  <ArrowLeft className="h-4 w-4 shrink-0 mr-1 md:h-5 md:w-5 md:mr-2" />
                  <span className="truncate max-w-[28vw] sm:max-w-none">
                    {backLabel}
                  </span>
                </Button>
              )}
            </div>

            {/* Center - Logo */}
            <div className="flex justify-center items-center gap-3">
              <img
                src={OdilonLogo}
                onClick={() => navigate("/")}
                alt="Chauvin Paris"
                className="h-12 w-auto cursor-pointer"
              />
              <h1 className="hidden mt-2 md:block text-2xl font-bold font-['Didot',serif] tracking-wide">
                CHAUVIN PARIS
              </h1>
            </div>

            {/* Right Side - User Menu */}
            <div className="flex items-center justify-end">
              {(displayName || user?.phone) && (
                <div className="relative" ref={dropdownRef}>
                  <button
                    onClick={() => setDropdownOpen(!dropdownOpen)}
                    className="flex max-w-[40vw] sm:max-w-none items-center gap-2 px-3 py-2 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
                    data-testid="topbar-user-button"
                    title={showsLocation && userLocation ? userLocation : displayName ?? undefined}
                  >
                    <User className="h-4 w-4 shrink-0 text-gray-600" />
                    <div className="min-w-0 text-left">
                      {showsLocation && userLocation ? (
                        // Boutique/Fleuriste/Atelier: Show location name
                        <p className="truncate text-sm font-semibold leading-tight">
                          {userLocation}
                        </p>
                      ) : (
                        // Admin/Senior/Comptable: Show name and role
                        // (the role line is hidden below sm to keep the bar one line tall)
                        <>
                          {displayName && (
                            <p className="truncate text-sm font-semibold leading-tight">
                              {displayName}
                            </p>
                          )}
                          {role && (
                            <p className="hidden sm:block truncate text-xs text-gray-600 leading-tight">
                              {role}
                            </p>
                          )}
                        </>
                      )}
                    </div>
                    <ChevronDown
                      className={`h-4 w-4 shrink-0 text-gray-600 transition-transform ${dropdownOpen ? "rotate-180" : ""}`}
                    />
                  </button>

                  {/* Dropdown Menu */}
                  {dropdownOpen && (
                    <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg border-2 border-gray-200 shadow-lg overflow-hidden">
                      {/* User Info Section */}
                      <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
                        {displayName && (
                          <p className="text-sm font-semibold text-gray-900">
                            {displayName}
                          </p>
                        )}
                        {role && (
                          <p className="text-xs text-gray-600 mt-0.5">{role}</p>
                        )}
                      </div>

                      {/* Logout Button */}
                      <button
                        onClick={handleLogoutClick}
                        className="w-full px-4 py-3 text-left hover:bg-red-50 transition-colors flex items-center gap-3 text-red-600 font-semibold"
                        data-testid="topbar-logout-button"
                      >
                        <LogOut className="h-5 w-5" />
                        {isShopUser
                          ? "Fermeture et déconnexion"
                          : "Déconnexion"}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
        {isShopUser && initialCheckError && (
          <FormError className="mx-4 mb-3 flex flex-wrap items-center justify-between gap-2 sm:mx-6">
            <p>{initialCheckError}</p>
            <Button
              variant="outline"
              disabled={isInitialCheckPending}
              onClick={() => setInitialCheckAttempt((attempt) => attempt + 1)}
              testID="cash-entry-initialization-retry"
            >
              {isInitialCheckPending ? "Vérification..." : "Réessayer"}
            </Button>
          </FormError>
        )}
      </div>
      {isShopUser && (
        <>
          <CashEntryModal
            isOpen={isStartModalOpen}
            onClose={closeStartModal}
            onSubmit={handleStartSubmit}
            onSkip={() => {
              closeStartModal();
              setHasEnteredStartAmount(false);
            }}
            title="Montant de caisse initial"
            description="Veuillez entrer le montant total en espèces dans la caisse au début de votre journée."
            submitText="Soumettre"
            hideSkipButton={!isSkipAllowed}
            isMandatory={isMandatory}
          />
          <CashEntryModal
            isOpen={isEndModalOpen}
            onClose={() => {
              setEndModalOpen(false);
              setEndAmountError(null);
            }}
            onSubmit={handleEndSubmit}
            onCancel={() => {
              setEndModalOpen(false);
              setEndAmountError(null);
            }}
            title="Montant de caisse final"
            description={
              endAmountError
                ? endAmountError
                : "Veuillez entrer le montant total en espèces dans la caisse à la fin de votre journée."
            }
            submitText="Soumettre et déconnecter"
            cancelText="Rester connecté"
            isMandatory={true}
          />
        </>
      )}
    </>
  );
}
